import BiobankData from 'BrdData/BiobankData';
import {
  BooleanAndNotesComponent,
  ContentWithNotesComponent,
  EditableDetailPanel,
} from 'Components';
import { ButtonFooter, HSCButton, UndoButton } from 'Components/Buttons';
import { Checkbox, Input, TextArea } from 'Components/HSCInput';
import { useAuthContext, useToast } from 'Context';
import { DataManager } from 'Lib/data';
import {
  useUndoableSaveAction,
  useUpdatableObject,
  useValidation,
} from 'Lib/hooks';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { getClickableLink } from 'Util';
import styles from './Biobanks.module.css';

let DM = new DataManager();

const BiobankInformationPanel = ({
  biobankId,
  biobankObject,
  title = 'Bio Resource Entry',
  editable = false,
  isNew = false,
  ...props
}) => {
  const { appUserId } = useAuthContext();
  const { showSaveSuccessToast } = useToast();

  const [selectedDate, setSelectedDate] = useState();
  const {
    currentObject: biobank,
    hasBeenUpdated,
    reset: resetBiobank,
    updateField,
    originalObject: originalBiobank,
    doSave,
  } = useUpdatableObject(
    BiobankData.updateRecord,
    BiobankData.getCreateBiobankFunction(appUserId),
    'BB',
  );

  const [resetUndo, showSaveUndoToast] = useUndoableSaveAction({
    originalObject: originalBiobank,
    undoFunction: BiobankData.updateRecord,
    onUndo: (originalBiobank) => fetchBiobank(originalBiobank.id),
    objectType: 'Bio Resource Entry',
  });

  const [editState, setEditState] = useState(undefined);

  useEffect(() => {
    DM.add(
      BiobankData.registerListener('BLANK_RECORD_LOADED', onBiobankLoaded),
      BiobankData,
    );
    DM.add(
      BiobankData.registerListener('RECORD_LOADED', onBiobankLoaded),
      BiobankData,
    );
    DM.add(
      BiobankData.registerListener('RECORD_CREATED', onBiobankCreated),
      BiobankData,
    );
    DM.add(
      BiobankData.registerListener('RECORD_UPDATED', onBiobankUpdated),
      BiobankData,
    );
    return () => {
      DM.clear();
    };
  }, []);

  let fields = {
    institution: {
      label: 'Institution',
      onEdit: (newValue) => {
        updateField('institution', newValue);
      },
    },

    indication: {
      label: 'Indication',
      isRequired: true,
      onEdit: (newValue) => {
        updateField('indication', newValue);
      },
    },

    in_scope: {
      label: 'In Scope',
      value: {
        render: (inEditState) => {
          return inEditState ? (
            <div className={styles.check_and_notes_container}>
              <Checkbox
                checked={biobank.in_scope}
                onValueChange={(checked) => {
                  updateField('in_scope', checked);
                }}
              />
              <Input
                placeholder={'Notes about scope'}
                value={biobank.in_scope_notes}
                onValueChange={(newValue) =>
                  updateField('in_scope_notes', newValue)
                }
                showClear={true}
                leftIconName={'note'}
              />
            </div>
          ) : (
            <BooleanAndNotesComponent
              extendedMode
              isTrue={biobank.in_scope}
              notes={biobank.in_scope_notes}
            />
          );
        },
      },
    },

    num_individuals: {
      label: 'N (Individuals)',
      value: {
        render: (inEditState) => {
          return inEditState ? (
            <div className={styles.check_and_notes_container}>
              <div style={{ flexShrink: 1, flexBasis: '15%' }}>
                <Input
                  placeholder={'N'}
                  value={biobank.num_individuals}
                  isNumericalInput
                  onValueChange={(newValue) =>
                    updateField('num_individuals', newValue)
                  }
                  showClear={true}
                />
              </div>
              <div style={{ flexGrow: 1, flexBasis: '80%' }}>
                <Input
                  placeholder={'Notes about N'}
                  value={biobank.num_individuals_notes}
                  onValueChange={(newValue) =>
                    updateField('num_individuals_notes', newValue)
                  }
                  showClear={true}
                  leftIconName={'note'}
                />
              </div>
            </div>
          ) : (
            <ContentWithNotesComponent
              extendedMode
              content={biobank.num_individuals}
              notes={biobank.num_individuals_notes}
            />
          );
        },
      },
    },

    molecular_profiling: {
      label: 'Molecular Profiling',
      value: {
        render: (inEditState) => {
          return inEditState ? (
            <div className={styles.check_and_notes_container}>
              <Checkbox
                checked={biobank.molecular_profiling}
                onValueChange={(checked) => {
                  updateField('molecular_profiling', checked);
                }}
              />
              <Input
                placeholder={'Notes about molecular profiling'}
                value={biobank.molecular_profiling_notes}
                onValueChange={(newValue) =>
                  updateField('molecular_profiling_notes', newValue)
                }
                showClear={true}
                leftIconName={'note'}
              />
            </div>
          ) : (
            <BooleanAndNotesComponent
              extendedMode
              isTrue={biobank.molecular_profiling}
              notes={biobank.molecular_profiling_notes}
            />
          );
        },
      },
    },

    biospecimens: {
      label: 'Biospecimens',
      onEdit: (newValue) => {
        updateField('biospecimens', newValue);
      },
    },

    link: {
      label: 'Link',
      format: (value, fieldName) =>
        fieldName === 'label'
          ? 'Link'
          : value && (
              <HSCButton
                iconOnly
                minimal
                href={getClickableLink(value)}
                target='_blank'
                tooltipText={value}
                iconName={'share'}
              />
            ),

      onEdit: (newValue) => {
        updateField('link', newValue);
      },
    },

    investigator: {
      label: 'Investigator(s)',
      onEdit: (newValue) => {
        updateField('investigator', newValue);
      },
    },

    department: {
      label: 'Department(s)',
      onEdit: (newValue) => {
        updateField('department', newValue);
      },
    },

    comments: {
      label: 'Comments',
      value: {
        render: (inEditState) => {
          return inEditState ? (
            <TextArea
              fill
              autoResize
              placeholder={'Comments'}
              value={biobank.comments}
              onValueChange={(newValue) => {
                updateField('comments', newValue);
              }}
              showClear
              onClear={() => updateField('comments', '')}
            />
          ) : (
            <div
              style={{
                whiteSpace: 'pre-line',
              }}>
              {biobank.comments}
            </div>
          );
        },
      },
    },
  };

  let { resetValidation, getValidatedDetailTable, ValidatedSaveButton } =
    useValidation(fields, biobank);

  useEffect(() => {
    if (isNew) {
      fetchBiobank(null);
    }
  }, [isNew]);

  useEffect(() => {
    if (biobankId && biobankId >= 0) {
      fetchBiobank(biobankId);
    }
  }, [biobankId]);

  useEffect(() => {
    if (!editable && biobankObject) {
      resetBiobank(biobankObject);
    } else if (biobankObject?.id && Number(biobankObject.id) >= 0) {
      fetchBiobank(biobankObject.id);
    }
  }, [biobankObject]);

  const onBiobankLoaded = (payload) => {
    if (payload) {
      resetBiobank(payload);
      resetUndo(() => fetchBiobank(payload.id));
      resetValidation();
    }
  };

  const fetchBiobank = (id) => {
    BiobankData.getBiobank(id);
  };

  const onBiobankCreated = (payload) => {
    if (payload) {
      showSaveSuccessToast('Bio Resource Entry Created');
      fetchBiobank(payload.id);
      setEditState(false);
    }
  };

  const onBiobankUpdated = (payload) => {
    if (payload) {
      showSaveUndoToast();
      setEditState(false);
    }
  };
  const undoAction = () => {
    fetchBiobank(biobank.id);
    resetValidation();
  };

  const undoButton = editable && hasBeenUpdated && (
    <UndoButton onClick={undoAction} />
  );

  const saveButton = editable && hasBeenUpdated && (
    <ValidatedSaveButton doSave={doSave} />
  );

  const footer = (
    <ButtonFooter>
      {undoButton}
      {saveButton}
    </ButtonFooter>
  );
  return (
    <EditableDetailPanel
      title={title}
      footer={footer}
      editable={editable}
      renderChild={getValidatedDetailTable}
      parentEditState={isNew || editState}
      {...props}
    />
  );
};

BiobankInformationPanel.propTypes = {
  biobankId: PropTypes.number,
  biobankObject: PropTypes.object,
  title: PropTypes.string,
  editable: PropTypes.bool,
  collapsible: PropTypes.bool,
  isNew: PropTypes.bool,
};

export default BiobankInformationPanel;
